import { type ThemeId } from "@forento/shared/models/platform"

import * as originalPages from "~/themes/original/pages"
import * as schoolPages from "~/themes/school/pages"
import r from "~/utilities/routes"

type AppRoute = { path: string; auth?: boolean; community?: boolean; page: Page }

type Page = { [theme in ThemeId]: React.ComponentType<object> }

type Pages = { [section: string]: { [page: string]: Page } }

const pages = {
	dashboard: {
		index: {
			legacy: originalPages.DashboardPage,
			modern: schoolPages.DashboardPage,
		},
	},
	billing: {
		index: { legacy: originalPages.BillingPage, modern: schoolPages.BillingPage },
	},
	bundle: {
		index: { legacy: originalPages.BundlesPage, modern: schoolPages.BundlesPage },
		detail: { legacy: originalPages.BundlePage, modern: schoolPages.BundlePage },
	},
	event: {
		index: { legacy: originalPages.EventsPage, modern: schoolPages.EventsPage },
		detail: { legacy: originalPages.EventPage, modern: schoolPages.EventPage },
	},
	certificate: {
		index: { legacy: originalPages.CertificatesPage, modern: schoolPages.CertificatesPage },
		detail: { legacy: originalPages.CertificatePage, modern: schoolPages.CertificatePage },
	},
	community: {
		index: { legacy: originalPages.CommunityPage, modern: schoolPages.CommunityPage },
		create: { legacy: originalPages.CreateProfilePage, modern: schoolPages.CreateProfilePage },
		post: { legacy: originalPages.CommunityPostPage, modern: schoolPages.CommunityPostPage },
		conversations: { legacy: originalPages.ConversationsPage, modern: schoolPages.ConversationsPage },
		conversation: { legacy: originalPages.ConversationsPage, modern: schoolPages.ConversationsPage },
		profile: { legacy: originalPages.ProfilePage, modern: schoolPages.ProfilePage },
		leaderboard: { legacy: originalPages.LeaderboardPage, modern: schoolPages.LeaderboardPage },
	},
	course: {
		index: { legacy: originalPages.CoursesPage, modern: schoolPages.CoursesPage },
		detail: { legacy: originalPages.CourseDetailPage, modern: schoolPages.CourseDetailPage },
		player: { legacy: originalPages.CoursePlayerPage, modern: schoolPages.CoursePlayerPage },
		exam: { legacy: originalPages.CourseExamPage, modern: schoolPages.CourseExamPage },
	},
	membership: {
		index: { legacy: originalPages.MembershipsPage, modern: schoolPages.MembershipsPage },
		detail: { legacy: originalPages.MembershipPage, modern: schoolPages.MembershipPage },
		completingPayPal: { legacy: originalPages.CompletingPayPalPage, modern: schoolPages.CompletingPayPalPage },
	},
	policy: {
		termsOfService: { legacy: originalPages.TermsOfServicePage, modern: schoolPages.TermsOfServicePage },
		privacy: { legacy: originalPages.PrivacyPolicyPage, modern: schoolPages.PrivacyPolicyPage },
		cookie: { legacy: originalPages.CookiePolicyPage, modern: schoolPages.CookiePolicyPage },
	},
	downloadable: {
		index: { legacy: originalPages.DownloadablesPage, modern: schoolPages.DownloadablesPage },
		detail: { legacy: originalPages.DownloadablePage, modern: schoolPages.DownloadablePage },
	},
	session: {
		index: { legacy: originalPages.SessionsPage, modern: schoolPages.SessionsPage },
		detail: { legacy: originalPages.SessionPage, modern: schoolPages.SessionPage },
	},
	user: {
		signin: { legacy: originalPages.SigninPage, modern: schoolPages.SigninPage },
		signup: { legacy: originalPages.SignupPage, modern: schoolPages.SignupPage },
		invitedSignupMembership: {
			legacy: originalPages.InvitedSignupMembership,
			modern: schoolPages.InvitedSignupMembership,
		},
		resetPassword: { legacy: originalPages.ResetPasswordPage, modern: schoolPages.ResetPasswordPage },
		newPassword: { legacy: originalPages.NewPasswordPage, modern: schoolPages.NewPasswordPage },
		completeProfile: { legacy: originalPages.CompleteProfilePage, modern: schoolPages.CompleteProfilePage },
		settings: { legacy: originalPages.SettingsPage, modern: schoolPages.SettingsPage },
	},
	video: {
		index: { legacy: originalPages.VideosPage, modern: schoolPages.VideosPage },
		detail: { legacy: originalPages.VideoPage, modern: schoolPages.VideoPage },
	},
} satisfies Pages

const notFoundPage: Page = { legacy: originalPages.NotFoundPage, modern: schoolPages.NotFoundPage }

const appRoutes: AppRoute[] = [
	{ path: r.billing.index(), page: pages.billing.index, auth: true },
	{ path: r.bundle.index(), page: pages.bundle.index },
	{ path: r.bundle.detail(null), page: pages.bundle.detail },
	{ path: r.certificate.index(), page: pages.certificate.index, auth: true },
	{ path: r.certificate.detail(null), page: pages.certificate.detail, auth: true },
	{ path: r.dashboard.index(), page: pages.dashboard.index },
	{ path: r.event.index(), page: pages.event.index },
	{ path: r.event.detail(null), page: pages.event.detail },
	{ path: r.community.index(), page: pages.community.index, auth: true, community: true },
	{ path: r.community.create(), page: pages.community.create, auth: true },
	{ path: r.community.post(null), page: pages.community.post, auth: true, community: true },
	{ path: r.community.conversations(), page: pages.community.conversations, auth: true, community: true },
	{ path: r.community.conversation(null), page: pages.community.conversation, auth: true, community: true },
	{ path: r.community.profile(null), page: pages.community.profile, auth: true, community: true },
	{ path: r.community.leaderboard(), page: pages.community.leaderboard, auth: true, community: true },
	{ path: r.course.index(), page: pages.course.index },
	{ path: r.course.detail(null), page: pages.course.detail },
	{ path: r.course.player(null), page: pages.course.player, auth: true },
	{ path: r.course.exam(null), page: pages.course.exam, auth: true },
	{ path: r.membership.index(), page: pages.membership.index },
	{ path: r.membership.detail(null), page: pages.membership.detail },
	{ path: r.membership.completingPayPal(), page: pages.membership.completingPayPal },
	{ path: r.policy.termsOfService(), page: pages.policy.termsOfService },
	{ path: r.policy.privacy(), page: pages.policy.privacy },
	{ path: r.policy.cookie(), page: pages.policy.cookie },
	{ path: r.downloadable.index(), page: pages.downloadable.index },
	{ path: r.downloadable.detail(null), page: pages.downloadable.detail },
	{ path: r.session.index(), page: pages.session.index },
	{ path: r.session.detail(null), page: pages.session.detail },
	{ path: r.user.signin(), page: pages.user.signin },
	{ path: r.user.signup(), page: pages.user.signup },
	{ path: r.user.invitedSignupMembership(), page: pages.user.invitedSignupMembership },
	{ path: r.user.resetPassword(), page: pages.user.resetPassword },
	{ path: r.user.newPassword(), page: pages.user.newPassword },
	{ path: r.user.completeProfile(), page: pages.user.completeProfile },
	{ path: r.user.settings(), page: pages.user.settings, auth: true },
	{ path: r.video.index(), page: pages.video.index },
	{ path: r.video.detail(null), page: pages.video.detail },
	{ path: "*", page: notFoundPage },
]

export default appRoutes
